/* eslint-disable no-console */
import * as Sentry from '@sentry/react';

const LOOKUP_API_URL = 'https://api.talview.com/v1/graphql';
const TIMEOUT_MS = 30000; // 30 seconds

// GraphQL query for svc_lookup
const SVC_LOOKUP_QUERY = `
  query lookup($hostname: String!) {
    svc_lookup(hostname: $hostname, application: FORM) {
      domain
    }
  }
`;

// GraphQL query for svc_config
const SVC_CONFIG_QUERY = `
  query getConfig {
    svc_config(application: FORM){
      application
      config {
        key
        value
      }
    }
  }
`;

// Default fallback configuration
// Uses process.env values if available, otherwise falls back to production defaults
const DEFAULT_FALLBACK_CONFIG = {
  REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL || 'https://supergraph.talview.com',
  HASURA_WS: process.env.HASURA_WS || 'wss://supergraph.talview.com/v1/graphql',
  REACT_APP_GROWTHBOOK_CLIENT_KEY: process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
  REACT_APP_GROWTHBOOK_API_HOST: process.env.GROWTHBOOK_API_HOST || 'https://gb-api.talview.com:443',
  REACT_APP_MIXPANEL_TOKEN: process.env.REACT_APP_MIXPANEL_TOKEN,
};

export interface RuntimeConfig {
  REACT_APP_BASE_URL: string;
  HASURA_WS: string;
  REACT_APP_GROWTHBOOK_CLIENT_KEY?: string;
  REACT_APP_GROWTHBOOK_API_HOST?: string;
  REACT_APP_MIXPANEL_TOKEN?: string;
  [key: string]: string | undefined;
}

/**
 * Fetch GraphQL query using native fetch
 * @param query - GraphQL query string
 * @param variables - Query variables
 * @param endpoint - GraphQL endpoint URL
 * @returns Promise with query result
 */
const fetchGraphQL = async <T>(query: string, variables: Record<string, unknown>, endpoint: string): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    return result.data;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Convert HTTP URL to WebSocket URL
 * @param httpUrl - HTTP URL (e.g., https://example.com/v1/graphql)
 * @returns WebSocket URL (e.g., wss://example.com/v1/graphql)
 */
const convertToWebSocketUrl = (httpUrl: string): string => httpUrl.replace(/^https?:\/\//, 'wss://');

/**
 * Fetch domain from svc_lookup query
 * @param hostname - Current hostname from window.location
 * @returns Promise with domain URL
 */
const fetchDomain = async (hostname: string): Promise<{ httpUrl: string; wsUrl: string }> => {
  try {
    console.log(`[RuntimeConfig] Fetching domain for hostname: ${hostname}`);

    const data = await fetchGraphQL<{ svc_lookup: { domain: string } }>(SVC_LOOKUP_QUERY, { hostname }, LOOKUP_API_URL);

    if (!data?.svc_lookup?.domain) {
      throw new Error('Invalid response from svc_lookup: missing domain');
    }

    const domain = data.svc_lookup.domain;

    const httpUrl = domain.startsWith('http://') || domain.startsWith('https://') ? domain : `https://${domain}`;
    const wsUrl = convertToWebSocketUrl(httpUrl);

    console.log('[RuntimeConfig] Successfully fetched domain:', {
      domain,
      httpUrl,
      wsUrl,
    });

    return { httpUrl, wsUrl };
  } catch (error) {
    console.error('[RuntimeConfig] Error fetching domain:', error);

    // Log to Sentry
    const sentryError = new Error(`Failed to fetch domain from svc_lookup for hostname: ${hostname}`);
    Sentry.captureException(sentryError, {
      extra: {
        hostname,
        originalError: error instanceof Error ? error.message : String(error),
      },
    });

    throw error;
  }
};

/**
 * Fetch configuration from svc_config query
 * @param hasuraHttp - HASURA HTTP endpoint to use for the query
 * @returns Promise with configuration object
 */
const fetchConfig = async (hasuraHttp: string): Promise<Partial<RuntimeConfig>> => {
  try {
    console.log('[RuntimeConfig] Fetching configuration from svc_config');

    const data = await fetchGraphQL<{
      svc_config: {
        application: string;
        config: Array<{ key: string; value: string }>;
      };
    }>(SVC_CONFIG_QUERY, {}, hasuraHttp);

    if (!data?.svc_config?.config) {
      throw new Error('Invalid response from svc_config: missing config array');
    }

    // Convert array of {key, value} to object
    const configObj: Partial<RuntimeConfig> = {};
    data.svc_config.config.forEach(({ key, value }) => {
      // Ignore HASURA_HTTP and HASURA_WS from svc_config as per requirement
      if (key !== 'HASURA_HTTP' && key !== 'HASURA_WS') {
        configObj[key] = value;
      }
    });

    console.log('[RuntimeConfig] Successfully fetched config:', Object.keys(configObj));

    return configObj;
  } catch (error) {
    console.error('[RuntimeConfig] Error fetching config:', error);

    // Log to Sentry
    const sentryError = new Error('Failed to fetch configuration from svc_config');
    Sentry.captureException(sentryError, {
      extra: {
        hasuraHttp,
        originalError: error instanceof Error ? error.message : String(error),
      },
    });

    throw error;
  }
};

/**
 * Get fallback configuration
 * @returns Fallback configuration object
 */
const getFallbackConfig = (): RuntimeConfig => {
  console.log('[RuntimeConfig] Using fallback configuration');
  return { ...DEFAULT_FALLBACK_CONFIG };
};

/**
 * Initialize runtime configuration
 * This is the main export that should be called during app initialization
 * @returns Promise with runtime configuration
 */
export const initializeRuntimeConfig = async (): Promise<RuntimeConfig> => {
  try {
    const hostname = window?.location?.hostname;

    if (!hostname) {
      console.warn('[RuntimeConfig] Unable to determine hostname, using fallback configuration');
      return getFallbackConfig();
    }

    let hasuraHttp: string;
    let hasuraWs: string;

    // Step 1: Fetch domain from svc_lookup
    try {
      const domainResult = await fetchDomain(hostname);
      hasuraHttp = domainResult.httpUrl;
      hasuraWs = domainResult.wsUrl;
    } catch (error) {
      console.warn('[RuntimeConfig] svc_lookup failed, using fallback URLs');
      hasuraHttp = DEFAULT_FALLBACK_CONFIG.REACT_APP_BASE_URL;
      hasuraWs = DEFAULT_FALLBACK_CONFIG.HASURA_WS;
    }

    // Step 2: Fetch additional config from svc_config
    let additionalConfig: Partial<RuntimeConfig> = {};
    try {
      const graphql_url = `${hasuraHttp}/v1/graphql`;
      additionalConfig = await fetchConfig(graphql_url);
    } catch (error) {
      console.warn('[RuntimeConfig] svc_config failed, using fallback config values including HASURA URLs');
      // Use fallback for additional config and also reset HASURA URLs to defaults
      const { REACT_APP_BASE_URL, HASURA_WS, ...fallbackAdditionalConfig } = DEFAULT_FALLBACK_CONFIG;
      additionalConfig = fallbackAdditionalConfig;
      // Reset HASURA URLs to defaults since fetchConfig failed
      hasuraHttp = DEFAULT_FALLBACK_CONFIG.REACT_APP_BASE_URL;
      hasuraWs = DEFAULT_FALLBACK_CONFIG.HASURA_WS;
    }

    // Step 3: Merge configurations
    const finalConfig: RuntimeConfig = {
      ...additionalConfig,
      REACT_APP_BASE_URL: hasuraHttp,
      HASURA_WS: hasuraWs,
    };

    console.log('[RuntimeConfig] Runtime configuration initialized successfully:', {
      REACT_APP_BASE_URL: finalConfig.REACT_APP_BASE_URL,
      HASURA_WS: finalConfig.HASURA_WS,
      configKeys: Object.keys(finalConfig),
    });

    return finalConfig;
  } catch (error) {
    console.error('[RuntimeConfig] Unexpected error during initialization:', error);
    Sentry.captureException(error);

    // Return complete fallback configuration
    return getFallbackConfig();
  }
};

/**
 * Get runtime configuration URLs
 * This is a convenience export for testing/debugging
 * @returns Promise with runtime configuration
 */
/**
 * Get runtime configuration URLs
 * This is a convenience export for testing/debugging
 * @returns Promise with runtime configuration
 */
export const getRuntimeConfig = initializeRuntimeConfig;

/**
 * Module-level cached runtime configuration
 * This is set by RuntimeConfigProvider after fetching config
 * Used by non-React code (utilities, sagas, helpers) that can't use hooks
 */
let cachedRuntimeConfig: RuntimeConfig | null = null;
let runtimeConfigReadyPromise: Promise<RuntimeConfig> | null = null;
let resolveRuntimeConfigReady: ((config: RuntimeConfig) => void) | null = null;

const ensureRuntimeConfigReadyPromise = (): Promise<RuntimeConfig> => {
  if (cachedRuntimeConfig) {
    return Promise.resolve(cachedRuntimeConfig);
  }

  if (!runtimeConfigReadyPromise) {
    runtimeConfigReadyPromise = new Promise<RuntimeConfig>((resolve) => {
      resolveRuntimeConfigReady = resolve;
    });
  }

  return runtimeConfigReadyPromise;
};

/**
 * Set the module-level runtime configuration
 * Called by RuntimeConfigProvider after config is fetched
 * @param config - Runtime configuration object
 */
export const setRuntimeConfig = (config: RuntimeConfig): void => {
  cachedRuntimeConfig = config;
  runtimeConfigReadyPromise = Promise.resolve(config);

  if (resolveRuntimeConfigReady) {
    resolveRuntimeConfigReady(config);
    resolveRuntimeConfigReady = null;
  }

  console.log('[RuntimeConfig] Module-level config cached for non-React code');
};

/**
 * Await runtime configuration initialization
 * Provides a promise that resolves once runtime config is available
 * @returns Promise resolving with runtime configuration
 */
export const waitForRuntimeConfig = async (): Promise<RuntimeConfig> => ensureRuntimeConfigReadyPromise();

/**
 * Get the module-level runtime configuration synchronously
 * For use in non-React code (utilities, sagas, helpers)
 * @throws Error if config is not initialized
 * @returns RuntimeConfig object
 */
export const getRuntimeConfigSync = (): RuntimeConfig => {
  if (!cachedRuntimeConfig) {
    throw new Error(
      'Runtime configuration not initialized. Make sure RuntimeConfigProvider is mounted and initialized.',
    );
  }
  return cachedRuntimeConfig;
};
